import http from "axios";

const Api = {
    baseUrl: null,
    cache: {},
    store: null,

    handleResponse(resolve, reject, response, options) {
        // expect object
        if (typeof response.data !== "object") {
            reject(Error("Response is not object but " + typeof response.data));
            return;
        }

        // handle response
        if (response.data && response.data.error && "AuthException".indexOf(response.data.error.type) === 0) {
            Api.store.dispatch("auth/tokenError");
            reject(Error(response.data.message));
            return;
        }

        // throw error to parent
        const ignoreErrors = options && options.ignoreErrors && false;
        if (!ignoreErrors) {
            if (response.data && response.data.error) {
                reject(response.data.error.message ? response.data.error.message : response.data.error);
                return;
            }
        }

        // handle successfully
        if (options && options.cache) {
            this.cache[options.cache] = response.data;
        }

        // request success, check for error in response
        resolve(response.data);
    },

    setToken(token) {
        http.defaults.headers.common.Authorization = token;
    },

    handleError(e, reject) {
        if (e.response && e.response.data && e.response.data.error && e.response.data.error.message) {
            const err = Error(e.response.data.error.message);
            err.code = e.response.data.code;
            reject(err);
        } else {
            reject(e);
        }
    },

    post(path, params, options, requestConfig) {
        const req = new Promise((resolve, reject) => {
            // return from cache
            if (options && options.cache && this.cache[options.cache]) {
                resolve(this.cache[options.cache]);
                return;
            }

            // post
            http.post(path, params, requestConfig).then((response) =>
                this.handleResponse(resolve, reject, response, options)
            ).catch((e) => {
                this.handleError(e, reject);
            });
        });
        return req;
    },

    upload(path, params, files, options) {
        const req = new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append("params", JSON.stringify(params));
            if (files) {
                for (const i in files) {
                    const file = files[i];

                    // file must be valid
                    if (!file) {
                        continue;
                    }

                    // check size
                    if (file.size > Api.maxFileSize) {
                        reject(Error("File is too big, current limit: " + Math.round(100 / 1024 / 1024) + "mB"));
                        return;
                    }
                    formData.append(i, file);
                }
            }
            http.post(path, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((response) =>
                this.handleResponse(resolve, reject, response, options)
            ).catch(e => {
                reject(e);
            });
        });
        return req;
    },

    get(path, options, requestConfig) {
        const req = new Promise((resolve, reject) => {
            // return from cache
            if (options && options.cache && this.cache[options.cache]) {
                resolve(this.cache[options.cache]);
                return;
            }

            // get
            http.get(path, requestConfig).then((response) =>
                this.handleResponse(resolve, reject, response, options)
            ).catch((e) => {
                this.handleError(e, reject);
            });
        });
        return req;
    },

    delete(path, options) {
        const req = new Promise((resolve, reject) => {
            http.delete(path).then((response) =>
                this.handleResponse(resolve, reject, response, options)
            ).catch((e) => {
                this.handleError(e, reject);
            });
        });
        return req;
    }
};

export default {
    init(baseURL, store) {
        Api.baseUrl = baseURL;
        Api.store = store;
        http.defaults.baseURL = baseURL;
    },

    setToken(token) {
        Api.setToken(token);
    },

    login(email, password) {
        return Api.post("auth/login", { email, password });
    },
    signup(name, email, privacy, token) {
        return Api.post("auth/signup", { name, email, privacy, token });
    },
    code(email, code) {
        return Api.post("auth/code", { email, code });
    },
    info() {
        return Api.get("auth/info");
    },
    update(params) {
        return Api.post("auth/info", params);
    },
    delete() {
        return Api.post("auth/delete", {});
    },

    avatars(params) {
        return Api.post("avatars", params);
    },
    avatarsCredits() {
        return Api.post("avatars/credits", {});
    },
    avatarsJobs(params) {
        return Api.post("avatars/jobs", params);
    },
    avatarsJobsRename(jobId, name, folder) {
        return Api.post("avatars/rename", { id: jobId, name, folder });
    },
    avatarsJobsDelete(jobId) {
        return Api.post("avatars/deleteJob", { id: jobId });
    },
    avatarsCreate(params) {
        return Api.post("avatars/create", params);
    },
    avatarsCreateFolder(folder) {
        return Api.post("avatars/create_folder", { folder });
    },
    avatarsDeleteFolder(folder) {
        return Api.post("avatars/delete_folder", { folder });
    },
    avatarsPreviewVoice(params) {
        return Api.post("avatars/preview_voice", params);
    },

    storage(params) {
        return Api.post("storage", params);
    },
    storageRename(fileId, filename, folder) {
        return Api.post("storage/rename", { id: fileId, name: filename, folder });
    },
    storageDeleteFile(fileId) {
        return Api.post("storage/deleteFile", { id: fileId });
    },
    storageUpload(files) {
        return Api.upload("storage/upload", {}, files);
    },
    storageCreateFolder(folder) {
        return Api.post("storage/create_folder", { folder });
    },
    storageDeleteFolder(folder) {
        return Api.post("storage/delete_folder", { folder });
    },

    billingPortal() {
        return Api.post("billing/portal", {});
    },
    billingPortalPackages() {
        return Api.post("billing", {});
    },
    billingSubscriptions() {
        return Api.post("billing/subscriptions", {});
    },
    billingInformation(params) {
        return Api.post("billing/information", params);
    },
    billingCountries() {
        return Api.post("billing/countries", {});
    },
    billingPaymentLink(priceId) {
        return Api.post("billing/paymentLink", { id: priceId });
    },

    knowledge(params) {
        return Api.post("knowledge", params);
    },
    knowledgeCreate(params) {
        return Api.post("knowledge/create", params);
    },
    knowledgeUpdate(params) {
        return Api.post("knowledge/update", params);
    },
    knowledgeFiles(knowledgeId) {
        return Api.post("knowledge/files", { knowledge_id: knowledgeId });
    },
    knowledgeUpload(files, knowledgeId) {
        return Api.upload("knowledge/upload", { knowledge_id: knowledgeId }, files);
    },
    knowledgeDelete(id) {
        return Api.post("knowledge/delete", { id });
    },
    knowledgeDeleteFile(fileId, knowledgeId) {
        return Api.post("knowledge/deleteFile", { id: fileId, knowledge_id: knowledgeId });
    },

    widgets(params) {
        return Api.post("widgets", params);
    },
    widgetsCreate(params) {
        return Api.post("widgets/create", params);
    },
    widgetsUpdate(params) {
        return Api.post("widgets/update", params);
    },
    widgetsDelete(id) {
        return Api.post("widgets/delete", { id });
    },

    voices(params) {
        return Api.post("voices", params);
    },

    languages(params) {
        return Api.post("widgets/languages", params);
    },

    history(params) {
        return Api.post("history", params);
    },
    historyMessages(id) {
        return Api.post("history/messages", { id });
    },
    historyExport() {
        return Api.post("history/export");
    }
};
